import React, { Component } from 'react';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import { compose, graphql } from 'react-apollo';
import { trackEvent } from 'googleTagManagerMiddleware';
import LoadingIndicator from 'components/LoadingIndicator';
import {
  dispatchGameSessionUpdated,
  dispatchSpinEvent
} from 'utils/gameEvents';
import { addToHistory } from 'utils/gameHistory';
import { gameShape, tournamentShape } from 'utils/shapes';
import './Game.css';

class Game extends Component {
  constructor(props) {
    super(props);

    this.state = {
      gameSession: undefined,
      sessionStarted: false,
      loading: false,
      error: undefined,
      startError: undefined
    };

    this.heartBeatInterval = null;
    this.iframeElement = null;
    this.endGameSessionCalled = false;

    this.handleUnload = this.handleUnload.bind(this);
    this.sendHeartBeat = this.sendHeartBeat.bind(this);
    this.handleMessageEvent = this.handleMessageEvent.bind(this);
    this.handleFrameLoad = this.handleFrameLoad.bind(this);
  }

  componentDidMount() {
    this.setState({ loading: true });

    addToHistory(this.props.game, this.props.tournament);

    this.makeGamingSesssionCall();

    window.addEventListener('beforeunload', this.handleUnload);
    window.addEventListener('message', this.handleMessageEvent);
  }

  handleSessionStart = () => {
    this.setState({
      sessionStarted: true,
      loading: false
    });
    this.heartBeatInterval = setInterval(
      this.sendHeartBeat,
      process.env.REACT_APP_GAME_HEARTBEAT_INTERVAL
    );
  };

  makeGamingSesssionCall = () => {
    this.props.makeGamingSession().then(({ data: { makeGamingSession } }) => {
      if (!makeGamingSession) {
        this.props.onError &&
          this.props.onError(
            'Failed to make gaming session. Please try again.'
          );
      } else {
        this.setState({
          gameSession: makeGamingSession
        });
      }

      if (this.props.onGamingSessionUpdated) {
        this.props.onGamingSessionUpdated(makeGamingSession);
      }

      dispatchGameSessionUpdated(this.state.gameSession);

      const { id } = makeGamingSession;
      if (id) {
        this.props
          .startGameSession(id)
          .then(({ data: { startGameSession } }) => {
            const { success, error } = startGameSession;

            if (!success) {
              this.setState({ startError: error });
            } else {
              this.handleSessionStart();
            }
          })
          .catch(e => this.setState({ startError: e }));
      } else {
        this.handleSessionStart();
      }
    });
  };

  endGamingSessionCall = () => {
    clearInterval(this.heartBeatInterval);

    if (this.state.gameSession && !this.endGameSessionCalled) {
      const {
        gameSession: { sessionId }
      } = this.state;
      this.endGameSessionCalled = true;
      this.props.endGamingSession(sessionId).catch(e => {
        console.error(e);
      });
    }
  };

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.handleUnload);
    window.removeEventListener('message', this.handleMessageEvent);
    this.endGamingSessionCall(true);
    dispatchGameSessionUpdated(null);
  }

  handleFrameLoad(e) {
    this.props.onFrameLoad && this.props.onFrameLoad(e, this.iframeElement);
  }

  handleUnload(e) {
    this.endGamingSessionCall(false);

    // Per spec, the beforeunload event handler should return null to avoid showing a dialog.
    // However, to accommodate IE, it must return undefined instead.
    return undefined;
  }

  sendHeartBeat() {
    this.props.sendGameSessionHeartbeat &&
      this.state.gameSession &&
      this.props.sendGameSessionHeartbeat(this.state.gameSession);

    trackEvent('game', 'heartbeat', this.props.game.name);
  }

  handleMessageEvent(event) {
    if (
      this.state.gameSession &&
      this.iframeElement &&
      this.iframeElement.contentWindow === event.source
    ) {
      dispatchSpinEvent(event.origin, event.data);
    }
  }

  render() {
    const { loading, gameSession, sessionStarted } = this.state;

    return (
      <div className="game">
        {loading && <LoadingIndicator expand />}
        {sessionStarted && gameSession && (
          <iframe
            key={this.props.gameReloadKey}
            title="Wind Creek Casino Game"
            className="game-frame"
            src={gameSession.gameUrl}
            ref={el => (this.iframeElement = el)}
            onLoad={this.handleFrameLoad}
            scrolling="no"
          />
        )}
      </div>
    );
  }
}

Game.propTypes = {
  game: gameShape.isRequired,
  makeGamingSession: PropTypes.func.isRequired,
  endGamingSession: PropTypes.func.isRequired,
  startGameSession: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  onGamingSessionUpdated: PropTypes.func,
  onFrameLoad: PropTypes.func,
  isTournament: PropTypes.bool,
  tournament: tournamentShape
};

const MUTATION_SEND_GAME_SESSION_HEARTBEAT = gql`
  mutation SendGameSessionHeartbeat($gameSession: SendGameSessionHeartbeat!) {
    sendGameSessionHeartbeat(gameSession: $gameSession) {
      success
    }
  }
`;

const MUTATION_MAKE_GAMING_SESSION = gql`
  mutation MakeGamingSession($input: MakeGamingSession!) {
    makeGamingSession(input: $input) {
      sessionId
      gameUrl
      id
    }
  }
`;

const MUTATION_START_GAME_SESSION = gql`
  mutation StartGameSession($gameSession: UpdateGameSession!) {
    startGameSession(gameSession: $gameSession) {
      success
      error
    }
  }
`;

const MUTATION_END_GAMING_SESSION = gql`
  mutation EndGamingSession($input: EndGamingSession!) {
    endGamingSession(input: $input)
  }
`;

const mapSendGameSessionHeartbeatProps = ({
  ownProps,
  mutateSendGameSessionHeartbeat
}) => ({
  sendGameSessionHeartbeat: gameSession => {
    return mutateSendGameSessionHeartbeat({
      variables: { gameSession: { id: gameSession.id } }
    });
  }
});

const mapMakeGamingSessionProps = ({ ownProps, mutateMakeGamingSession }) => ({
  makeGamingSession: () => {
    const gamingSession = { gameId: ownProps.game.id };

    if (ownProps.tournament && ownProps.tournament.id) {
      gamingSession.tournamentId = ownProps.tournament.id;
    }

    return mutateMakeGamingSession({
      variables: { input: gamingSession }
    });
  }
});

const mapStartGameSessionProps = ({ mutateStartGameSession }) => ({
  startGameSession: (id, vendorId) =>
    mutateStartGameSession({
      variables: { gameSession: { id, vendorId } }
    })
});

const mapEndGamingSessionProps = ({ mutateEndGamingSession }) => ({
  endGamingSession: sessionId =>
    mutateEndGamingSession({
      variables: { input: { sessionId } }
    })
});

export default compose(
  graphql(MUTATION_MAKE_GAMING_SESSION, {
    name: 'mutateMakeGamingSession',
    props: mapMakeGamingSessionProps
  }),
  graphql(MUTATION_START_GAME_SESSION, {
    name: 'mutateStartGameSession',
    props: mapStartGameSessionProps
  }),
  graphql(MUTATION_SEND_GAME_SESSION_HEARTBEAT, {
    name: 'mutateSendGameSessionHeartbeat',
    props: mapSendGameSessionHeartbeatProps
  }),
  graphql(MUTATION_END_GAMING_SESSION, {
    name: 'mutateEndGamingSession',
    props: mapEndGamingSessionProps
  })
)(Game);
